<template>
  <BFormGroup
    :label="displayLabel"
    :label-for="inputId"
    :invalid-feedback="validatedErrorMessage"
    :state="inputState"
    :description="description"
    :class="{ 'required-field': required }"
  >
    <BFormInput
      :id="inputId"
      :model-value="displayValue"
      :type="type"
      :step="step"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :state="inputState"
      :size="size"
      :autocomplete="autocomplete"
      :maxlength="maxlength"
      @update:model-value="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
    />
  </BFormGroup>
</template>

<script setup>
/**
 * BaseInput - 基本入力フィールドコンポーネント (Atom)
 *
 * アプリケーション全体で使用される再利用可能な基本入力フィールドです。
 * Bootstrap Vueのb-form-inputとb-form-groupをラップして、
 * アプリケーション固有のバリデーションルールやデザインを適用します。
 *
 * Vue 3 Composition API:
 * - <script setup>構文を使用
 * - defineProps, defineEmitsで型安全性を確保
 * - computedでリアクティブな算出プロパティを実装
 * - 改善されたバリデーション機能
 * - オートコンプリート対応
 * - Enterキーイベント対応
 *
 * 使用例:
 * <BaseInput
 *   v-model="formData.username"
 *   label="ユーザー名"
 *   type="text"
 *   placeholder="ユーザー名を入力してください"
 *   required
 *   :validator="validateUsername"
 *   @enter="handleSubmit"
 * />
 *
 * 責務:
 * - UIの描画に専念（ロジックは持たない）
 * - v-modelで双方向データバインディング
 * - emitで親に各種イベントを通知する
 * - 一貫したフォームバリデーション表示
 * - アクセシビリティの確保
 */
import { computed, getCurrentInstance } from 'vue'

/**
 * Props定義
 */
const props = defineProps({
  /**
   * 入力フィールドの値（Vue 3 v-model）
   */
  modelValue: {
    type: [String, Number],
    default: ''
  },
  /**
   * ラベルテキスト
   */
  label: {
    type: String,
    default: ''
  },
  /**
   * 入力フィールドのタイプ
   * 'text': 通常のテキスト入力
   * 'email': メールアドレス入力
   * 'password': パスワード入力
   * 'number': 数値入力（緯度・経度など）
   * 'tel': 電話番号入力
   * 'url': URL入力
   */
  type: {
    type: String,
    default: 'text',
    validator: (value) => {
      return ['text', 'email', 'password', 'number', 'tel', 'url'].includes(value)
    }
  },
  /**
   * プレースホルダーテキスト
   */
  placeholder: {
    type: String,
    default: ''
  },
  /**
   * 数値入力の刻み幅（number typeの場合）
   */
  step: {
    type: [String, Number],
    default: null
  },
  /**
   * 小数点以下の桁数（number typeの場合）
   * 指定した桁数で数値を表示します
   */
  decimalPlaces: {
    type: Number,
    default: null
  },
  /**
   * 入力フィールドを無効化するかどうか
   */
  disabled: {
    type: Boolean,
    default: false
  },
  /**
   * 入力フィールドを読み取り専用にするかどうか
   */
  readonly: {
    type: Boolean,
    default: false
  },
  /**
   * 入力フィールドのサイズ
   * 'sm': 小サイズ（テーブル内入力など）
   * 'md': 標準サイズ（一般的な入力フィールド）
   * 'lg': 大サイズ（重要な入力フィールド）
   */
  size: {
    type: String,
    default: 'md',
    validator: (value) => {
      return ['sm', 'md', 'lg'].includes(value)
    }
  },
  /**
   * エラーメッセージ
   * 設定されている場合は入力フィールドがエラー状態で表示される
   */
  errorMessage: {
    type: String,
    default: ''
  },
  /**
   * 説明テキスト
   * 入力フィールドの下に表示される補足情報
   */
  description: {
    type: String,
    default: ''
  },
  /**
   * 必須フィールドかどうか
   * trueの場合、ラベルに必須マークが表示される
   */
  required: {
    type: Boolean,
    default: false
  },
  /**
   * オートコンプリート属性
   * ブラウザのオートコンプリート機能を制御
   */
  autocomplete: {
    type: String,
    default: 'off'
  },
  /**
   * バリデーション関数
   * 入力値の検証を行う関数を指定可能
   */
  validator: {
    type: Function,
    default: null
  },
  /**
   * 最大入力文字数
   */
  maxlength: {
    type: Number,
    default: 20
  }
})

/**
 * Emits定義
 */
const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'enter'])

/**
 * 現在のコンポーネントインスタンスを取得（uidを使用するため）
 */
const instance = getCurrentInstance()

/**
 * 一意なIDを生成して、labelとinputを関連付ける
 */
const inputId = computed(() => `base-input-${instance.uid}`)

/**
 * 必須マーク付きのラベルを表示
 */
const displayLabel = computed(() => {
  return props.required ? `${props.label} *` : props.label
})

/**
 * 表示用の値をフォーマット
 * decimalPlacesが指定されている場合は小数点以下の桁数を固定
 */
const displayValue = computed(() => {
  const currentValue = props.modelValue
  if (props.type === 'number' && props.decimalPlaces !== null) {
    if (currentValue === '' || currentValue === null || currentValue === undefined) {
      return ''
    }
    const numValue = Number(currentValue)
    return isNaN(numValue) ? '' : numValue.toFixed(props.decimalPlaces)
  }
  return currentValue
})

/**
 * バリデーション済みのエラーメッセージ
 */
const validatedErrorMessage = computed(() => {
  if (props.errorMessage) {
    return props.errorMessage
  }
  if (props.validator && props.modelValue) {
    const validationResult = props.validator(props.modelValue)
    return validationResult === true ? '' : validationResult
  }
  return ''
})

/**
 * Bootstrap Vueのstate（バリデーション状態）を決定
 */
const inputState = computed(() => {
  if (validatedErrorMessage.value) {
    return false // エラー状態
  }
  return null // 通常状態
})

/**
 * 入力値変更イベントを親コンポーネントに通知
 * @param {string|number} newValue - 新しい入力値
 */
const handleInput = (newValue) => {
  emit('update:modelValue', newValue)
}

/**
 * Enterキー押下イベントを親コンポーネントに通知
 * @param {Event} event - キーボードイベント
 */
const handleEnter = (event) => {
  emit('enter', event)
}

/**
 * フォーカスイベントを親コンポーネントに通知
 * @param {Event} event - フォーカスイベント
 */
const handleFocus = (event) => {
  emit('focus', event)
}

/**
 * ブラーイベントを親コンポーネントに通知
 * @param {Event} event - ブラーイベント
 */
const handleBlur = (event) => {
  emit('blur', event)
}
</script>

<style lang="scss" scoped>
/* BaseInput コンポーネントのスタイル */

/* 必須フィールドのスタイル */
.required-field {
  :deep(label) {
    color: #495057;
    font-weight: 500;

    &::after {
      content: '';
      color: #dc3545;
      margin-left: 2px;
    }
  }
}

/* フォーカス時の強調表示 */
:deep(.form-control) {
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  }

  &.is-invalid {
    border-color: #dc3545;

    &:focus {
      border-color: #dc3545;
      box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
    }
  }
}

/* 説明テキストのスタイル */
:deep(.form-text) {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

/* エラーメッセージのスタイル */
:deep(.invalid-feedback) {
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
}

/* 読み取り専用状態のスタイル */
:deep(.form-control[readonly]) {
  background-color: #f8f9fa;
  opacity: 1;
}

/* 無効状態のスタイル */
:deep(.form-control:disabled) {
  background-color: #e9ecef;
  opacity: 1;
}
</style>
